<template>
  <div>
    <!-- Breakdown Options -->
    <b-row>
      <b-col>
        <b-card>
          <div class="d-flex flex-column">
            <b-card-title class="mb-0">
              Product Breakdowns
            </b-card-title>
            <div class="font-small-3 mb-1 text-muted">
              Charts are generated from the subset choices below.
            </div>
            <div class="d-flex flex-wrap mt-2 align-items-center">
              <div class="d-flex align-items-center">
                <span class="mr-1">Product subset</span>
                <b-dropdown
                  :text="selectedSubsetLabel"
                  variant="outline-primary"
                  toggle-class="text-left d-flex justify-content-between align-items-center"
                  menu-class="w-100"
                  style="min-width: 150px;"
                >
                  <b-dropdown-item
                    v-for="option in productSubset.options"
                    :key="option.value"
                    :active="productSubset.selectedSubset === option.value"
                    @click="productSubset.selectedSubset = option.value"
                  >
                    {{ option.label }}
                  </b-dropdown-item>
                </b-dropdown>
              </div>
              <div
                v-if="productSubset.selectedSubset !== 'Live'"
                class="d-flex flex-wrap align-items-center"
              >
                <span
                  class="pr-1 mt-1 pl-0 pl-xl-3 mt-xl-0"
                  style="max-width: 300px;"
                >
                  <span v-if="productSubset.selectedSubset === 'All'">{{ allProductsSubsetDescription }}</span>
                  <span v-if="productSubset.selectedSubset === 'Matured'">Products that matured in the selected range</span>
                </span>
                <div class="d-flex align-items-center my-1 my-md-0 justify-content-end">
                  <span
                    class="mr-1"
                    :class="{ 'text-muted': portfolioType === 'u' }"
                  >From</span>
                  <span
                    v-if="portfolioType === 'u'"
                    class="breakdown-date-static"
                  >
                    {{ formatBreakdownDate(productSubset.dateFrom) }}
                  </span>
                  <b-form-datepicker
                    v-else
                    v-model="productSubset.dateFrom"
                    placeholder="From"
                    style="width: 200px;"
                    :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
                    reset-button
                    :reset-value="''"
                    label-reset-button="Clear"
                    offset="160"
                    locale="en-GB"
                    right
                  />
                </div>
                <div class="d-flex align-items-center justify-content-end">
                  <span
                    class="mx-1"
                    :class="{ 'text-muted': portfolioType === 'u' }"
                  >To</span>
                  <span
                    v-if="portfolioType === 'u'"
                    class="breakdown-date-static"
                  >
                    {{ formatBreakdownDate(productSubset.dateTo) }}
                  </span>
                  <b-form-datepicker
                    v-else
                    v-model="productSubset.dateTo"
                    placeholder="To"
                    style="width: 200px;"
                    :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
                    reset-button
                    :reset-value="''"
                    label-reset-button="Clear"
                    offset="160"
                    locale="en-GB"
                    right
                  />
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Breakdown Charts -->
    <b-overlay
      :show="loading"
      rounded
      opacity="0.3"
      spinner-variant="primary"
    >
      <b-row class="match-height mt-0">
        <b-col md="4">
          <b-card>
            <b-card-title class="mb-0">
              Underlyings
            </b-card-title>
            <div class="font-small-3 mb-1 text-muted">
              {{ breakdownPrefix }} breakdown by underlying showing proportion and return
            </div>
            <b-row class="match-height">
              <b-col cols="7">
                <app-echart-bar
                  :option-data="breakdown.underlyings.holdingOptions"
                  :height="chartHeights.underlyings + 'px'"
                />
              </b-col>
              <b-col cols="5">
                <app-echart-bar
                  :option-data="breakdown.underlyings.returnOptions"
                  :height="chartHeights.underlyings + 'px'"
                />
              </b-col>
            </b-row>
          </b-card>
        </b-col>

        <b-col md="4">
          <b-card>
            <b-card-title class="mb-0">
              Counterparties
            </b-card-title>
            <div class="font-small-3 mb-1 text-muted">
              {{ breakdownPrefix }} breakdown by counterparty showing proportion and return
            </div>
            <b-row class="match-height">
              <b-col cols="7">
                <app-echart-bar
                  :option-data="breakdown.issuers.holdingOptions"
                  :height="chartHeights.issuers + 'px'"
                />
              </b-col>
              <b-col cols="5">
                <app-echart-bar
                  :option-data="breakdown.issuers.returnOptions"
                  :height="chartHeights.issuers + 'px'"
                />
              </b-col>
            </b-row>
          </b-card>
        </b-col>

        <b-col md="4">
          <b-card>
            <b-card-title class="mb-0">
              Product Types
            </b-card-title>
            <div class="font-small-3 mb-1 text-muted">
              {{ breakdownPrefix }} breakdown by type showing proportion and return
            </div>
            <b-row class="match-height">
              <b-col cols="7">
                <app-echart-bar
                  :option-data="breakdown.productTypes.holdingOptions"
                  :height="chartHeights.productTypes + 'px'"
                />
              </b-col>
              <b-col cols="5">
                <app-echart-bar
                  :option-data="breakdown.productTypes.returnOptions"
                  :height="chartHeights.productTypes + 'px'"
                />
              </b-col>
            </b-row>
          </b-card>
        </b-col>
      </b-row>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardTitle, BRow, BCol, BOverlay, BFormDatepicker, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import AppEchartBar from '@core/components/charts/echart/AppEchartBar.vue'
import { $themeColors } from '@themeConfig'
import useAppConfig from '@core/app-config/useAppConfig'
import _ from 'lodash'

export default {
  components: {
    BCard,
    BCardTitle,
    BRow,
    BCol,
    BOverlay,
    BFormDatepicker,
    BDropdown,
    BDropdownItem,
    AppEchartBar,
  },
  props: {
    portfolioType: {
      type: [String, Number],
      default: 'u',
    },
    productWeightMode: {
      type: String,
      default: 'default',
    },
    searchFilter: {
      type: Object,
      default: null,
    },
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin }
  },
  data() {
    return {
      loading: false,
      breakdownRequestId: 0,
      appliedBreakdownKey: null,
      productSubset: {
        options: [
          { label: 'All', value: 'All' },
          { label: 'Matured', value: 'Matured' },
          { label: 'Live', value: 'Live' },
        ],
        selectedSubset: 'All',
        dateFrom: 'default',
        dateTo: 'default',
      },
      breakdown: {
        underlyings: {
          holdingOptions: null,
          returnOptions: null,
        },
        issuers: {
          holdingOptions: null,
          returnOptions: null,
        },
        productTypes: {
          holdingOptions: null,
          returnOptions: null,
        },
      },
      chartHeights: {
        underlyings: 400,
        issuers: 400,
        productTypes: 200,
      },
    }
  },
  computed: {
    splitLineOpacity() {
      return this.skin === 'dark' ? 0.1 : 1
    },
    breakdownPrefix() {
      return this.portfolioType === 'u' ? 'Marketplace' : 'Portfolio'
    },
    selectedSubsetLabel() {
      const selected = this.productSubset.options.find(opt => opt.value === this.productSubset.selectedSubset)
      return selected ? selected.label : 'All'
    },
    allProductsSubsetDescription() {
      return this.portfolioType === 'u'
        ? 'Live products plus those matured in the range shown'
        : 'Live products plus those matured in the selected range'
    },
    breakdownConstraints() {
      return {
        dateFrom: this.productSubset.dateFrom,
        dateTo: this.productSubset.dateTo,
        showSet: this.productSubset.selectedSubset,
      }
    },
    breakdownRequestKey() {
      return JSON.stringify({
        portfolioType: this.portfolioType,
        productWeightMode: this.productWeightMode,
        searchFilter: this.portfolioType === 'v' ? this.searchFilter : null,
        constraints: this.breakdownConstraints,
        skin: this.skin,
      })
    },
  },
  watch: {
    breakdownRequestKey(key) {
      // Applying server-normalized dates does not require another request.
      if (key !== this.appliedBreakdownKey) this.loadBreakdown()
    },
  },
  created() {
    this.loadBreakdownDebounced = _.debounce(this.fetchBreakdown, 500)
  },
  mounted() {
    this.loadBreakdown()
  },
  beforeDestroy() {
    this.breakdownRequestId += 1
    this.loadBreakdownDebounced.cancel()
  },
  methods: {
    formatBreakdownDate(value) {
      if (!value || value === 'default') return '-'

      let date = new Date(value)
      if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
        const [year, month, day] = value.slice(0, 10).split('-').map(Number)
        date = new Date(year, month - 1, day)
      }

      if (Number.isNaN(date.getTime())) return value

      return new Intl.DateTimeFormat('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(date)
    },
    loadBreakdown() {
      // Invalidate in-flight responses immediately, before the debounce delay.
      this.breakdownRequestId += 1
      this.appliedBreakdownKey = null
      this.loading = true
      Object.keys(this.breakdown).forEach(key => {
        this.breakdown[key].holdingOptions = null
        this.breakdown[key].returnOptions = null
      })
      this.loadBreakdownDebounced(this.breakdownRequestId, this.breakdownRequestKey)
    },
    fetchBreakdown(requestId, requestKey) {
      let currentRequestKey = requestKey
      const isCurrentRequest = () => requestId === this.breakdownRequestId
        && currentRequestKey === this.breakdownRequestKey
      if (!isCurrentRequest()) return Promise.resolve()

      const {
        portfolioType, productWeightMode, searchFilter, constraints,
      } = JSON.parse(requestKey)

      // Determine which API endpoint to use based on portfolio type
      let breakdownEndpoint
      const breakdownParams = {
        productweightingmode: productWeightMode,
        constraints,
      }

      if (portfolioType === 'u') {
        breakdownEndpoint = 'lifecycle/public/report.cfc?method=getUniversePortfolioReportBreakdown'
      } else if (portfolioType === 'v') {
        breakdownEndpoint = 'lifecycle/public/report.cfc?method=getVirtualPortfolioReportBreakdown'
        // Virtual portfolios need searchFilter
        breakdownParams.searchfilter = JSON.stringify(searchFilter)
      } else {
        breakdownEndpoint = 'lifecycle/public/report.cfc?method=getUserPortfolioReportBreakdown'
        breakdownParams.portfolioid = portfolioType
      }

      return this.$http
        .get(breakdownEndpoint, { params: breakdownParams })
        .then(response => {
          if (!isCurrentRequest()) return
          this.fillBreakdown(response.data)
          currentRequestKey = this.breakdownRequestKey
          this.appliedBreakdownKey = currentRequestKey
        })
        .catch(err => {
          if (!isCurrentRequest()) return
          console.error('Error loading breakdown data:', err)
        })
        .finally(() => {
          if (!isCurrentRequest()) return
          this.loading = false
        })
    },
    fillBreakdown(data) {
      // Convert and set underlyings
      this.breakdown.underlyings.holdingOptions = this.convertToEChartsOptions(
        data.portfoliosummary.underlyings.holdingseries,
        'Proportion',
        $themeColors.primary,
      )
      this.breakdown.underlyings.returnOptions = this.convertToEChartsOptions(
        data.portfoliosummary.underlyings.returnseries,
        'Return',
        $themeColors.warning,
        true,
      )

      // Convert and set issuers
      this.breakdown.issuers.holdingOptions = this.convertToEChartsOptions(
        data.portfoliosummary.issuers.holdingseries,
        'Proportion',
        $themeColors.primary,
      )
      this.breakdown.issuers.returnOptions = this.convertToEChartsOptions(
        data.portfoliosummary.issuers.returnseries,
        'Return',
        $themeColors.warning,
        true,
      )

      // Convert and set product types
      this.breakdown.productTypes.holdingOptions = this.convertToEChartsOptions(
        data.portfoliosummary.producttypes.holdingseries,
        'Proportion',
        $themeColors.primary,
      )
      this.breakdown.productTypes.returnOptions = this.convertToEChartsOptions(
        data.portfoliosummary.producttypes.returnseries,
        'Return',
        $themeColors.warning,
        true,
      )

      this.productSubset.dateFrom = data.portfoliosummary.datefrom
      this.productSubset.dateTo = data.portfoliosummary.dateto

      this.updateBarChartHeights(data)
    },
    convertToEChartsOptions(apexSeries, xAxisLabel, color, hideYAxis = false) {
      if (!apexSeries || apexSeries.length === 0) {
        return {
          legend: { show: false },
          grid: {
            left: 70, right: 30, bottom: 50, top: 20,
          },
          xAxis: { type: 'value' },
          yAxis: { type: 'category', data: [] },
          series: [],
        }
      }

      // Extract categories and data from ApexCharts format
      const categories = apexSeries[0].data.map(item => item.x)
      const values = apexSeries[0].data.map(item => {
        // Convert string values to numbers
        const numValue = typeof item.y === 'string' ? parseFloat(item.y) : item.y
        return numValue
      })

      const options = {
        legend: { show: false },
        grid: {
          left: hideYAxis ? 30 : 160,
          right: 30,
          bottom: 50,
          top: 20,
          containLabel: false,
        },
        xAxis: {
          type: 'value',
          name: xAxisLabel,
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontSize: 11,
            fontWeight: 500,
          },
          axisLabel: {
            fontSize: 10,
          },
          axisLine: {
            lineStyle: {
              opacity: 0.7,
            },
          },
          axisTick: {
            lineStyle: {
              opacity: 0.1,
            },
          },
          splitLine: {
            show: false,
          },
        },
        yAxis: {
          type: 'category',
          data: categories,
          axisLabel: {
            show: !hideYAxis,
            fontSize: 10,
            margin: 20,
            formatter: value => {
              const maxLength = 20
              if (value.length > maxLength) {
                return `${value.substring(0, maxLength)}...`
              }
              return value
            },
          },
          axisTick: {
            show: !hideYAxis,
            lineStyle: {
              opacity: 0.1,
            },
          },
          axisLine: {
            show: !hideYAxis,
            lineStyle: {
              opacity: 0.1,
              width: 1,
            },
          },
          splitLine: {
            show: true,
            lineStyle: {
              opacity: this.splitLineOpacity,
            },
          },
        },
        series: [
          {
            type: 'bar',
            data: values,
            barWidth: '70%',
            itemStyle: {
              color,
            },
            label: {
              show: true,
              position: 'inside',
              formatter: params => {
                const { value } = params
                if (typeof value === 'number') {
                  return parseFloat(value.toFixed(2))
                }
                return value
              },
              fontSize: 10,
              color: '#ffffff',
            },
          },
        ],
      }

      return options
    },
    updateBarChartHeights(data) {
      const numUnderlyings = data.portfoliosummary.underlyings.holdingseries[0]?.data?.length || 0
      if (numUnderlyings < 4) {
        this.chartHeights.underlyings = 120
      } else {
        this.chartHeights.underlyings = numUnderlyings * 26
      }

      const numIssuers = data.portfoliosummary.issuers.holdingseries[0]?.data?.length || 0
      if (numIssuers < 4) {
        this.chartHeights.issuers = 120
      } else {
        this.chartHeights.issuers = numIssuers * 26
      }

      const numProductTypes = data.portfoliosummary.producttypes.holdingseries[0]?.data?.length || 0
      if (numProductTypes < 4) {
        this.chartHeights.productTypes = 100
      } else {
        this.chartHeights.productTypes = numProductTypes * 22
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.breakdown-date-static {
  display: inline-block;
  min-width: 110px;
  padding: 0.438rem 0;
  font-weight: 600;
  line-height: 1.45;
}
</style>
